import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { handleCartItemUpdate } from "../utils/cartUpdateUtils";
import {
  calculateBasePrice,
  calculateOptionsSurcharge,
} from "../utils/cartCalculationUtils";

type PendingUpdate = {
  item: any;
  quantity: number;
  timestamp: number;
};

/**
 * Unified hook for debouncing cart updates across both Cart and MiniCart
 * Provides optimistic UI updates while batching API calls
 *
 * @param debounceTime - Time in ms to wait before sending updates (default: 1000ms)
 * @param useCartItemApi - Whether to use CartItemUpdate hooks directly (for Cart) or handleCartItemUpdate util (for MiniCart)
 * @returns Object with methods to manage cart updates
 */
export function useDebounceCartUpdates(
  debounceTime = 1000,
  useCartItemApi = false
) {
  const dispatch = useDispatch();

  // Track pending updates by item ID
  const pendingUpdatesRef = useRef<Record<string, PendingUpdate>>({});

  // Track timeouts
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track optimistic UI state
  const [optimisticState, setOptimisticState] = useState<
    Record<string, number>
  >({});

  // Track optimistic price calculations
  const [optimisticPrices, setOptimisticPrices] = useState<
    Record<string, { basePrice: number; totalPrice: number }>
  >({});

  // Import CartItemUpdate hooks lazily if needed
  const cartItemUpdateRef = useRef<any>(null);

  useEffect(() => {
    // Only import if we're using the direct API hooks
    if (useCartItemApi && !cartItemUpdateRef.current) {
      import("./useCartItemUpdate").then((module) => {
        const { updateQuantity, removeItem } = module.default();
        cartItemUpdateRef.current = { updateQuantity, removeItem };
      });
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [useCartItemApi]);

  /**
   * Calculate optimistic prices for an item
   */
  const calculateOptimisticPrices = useCallback((item: any) => {
    // Calculate base price
    const basePrice = calculateBasePrice(item);

    // Calculate options surcharge
    const optionSurcharge = calculateOptionsSurcharge(item);

    // Calculate total price
    const totalPrice = basePrice + optionSurcharge;

    return { basePrice, totalPrice };
  }, []);

  /**
   * Process all pending updates at once
   */
  const processUpdates = useCallback(async () => {
    const updates = Object.values(pendingUpdatesRef.current);

    // Sort updates - removals first, then by timestamp
    updates.sort((a, b) => {
      // Removals (quantity 0) first
      if (a.quantity === 0 && b.quantity > 0) return -1;
      if (b.quantity === 0 && a.quantity > 0) return 1;
      // Otherwise sort by timestamp (oldest first)
      return a.timestamp - b.timestamp;
    });

    // Clear pending updates before processing
    pendingUpdatesRef.current = {};

    // Process all updates
    for (const update of updates) {
      if (useCartItemApi && cartItemUpdateRef.current) {
        // Using direct API hooks
        if (update.quantity === 0) {
          await cartItemUpdateRef.current.removeItem(update.item);
        } else {
          await cartItemUpdateRef.current.updateQuantity(
            update.item,
            update.quantity
          );
        }
      } else {
        // Using generic cart update utility
        await handleCartItemUpdate(update.quantity, update.item, dispatch);
      }
    }

    // Clear optimistic state after processing
    setOptimisticState({});
    setOptimisticPrices({});
  }, [dispatch, useCartItemApi]);

  /**
   * Queue an update for an item
   */
  const queueUpdate = useCallback(
    (item: any, newQuantity: number) => {
      // Skip for optimistic items or invalid items
      if (!item || !item.cartCustomId || item.isOptimistic) return newQuantity;

      const itemId = item.cartCustomId;

      // Update optimistic UI state immediately
      setOptimisticState((prev) => ({
        ...prev,
        [itemId]: newQuantity,
      }));

      // Calculate and update optimistic prices
      const prices = calculateOptimisticPrices(item);
      setOptimisticPrices((prev) => ({
        ...prev,
        [itemId]: prices,
      }));

      // Add to pending updates
      pendingUpdatesRef.current[itemId] = {
        item,
        quantity: newQuantity,
        timestamp: Date.now(),
      };

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout
      timeoutRef.current = setTimeout(() => {
        processUpdates();
        timeoutRef.current = null;
      }, debounceTime);

      return newQuantity;
    },
    [debounceTime, processUpdates, calculateOptimisticPrices]
  );

  /**
   * Handle quantity change (can be used for both increase and decrease)
   */
  const handleQuantityChange = useCallback(
    (item: any, delta: number) => {
      const itemId = item.cartCustomId;
      const currentOptimisticQty = optimisticState[itemId] ?? item.quantity;
      const newQuantity = Math.max(0, currentOptimisticQty + delta);

      // Don't exceed available stock
      const availableStock = item.product?.stock || 0;
      const limitedQuantity = Math.min(newQuantity, availableStock);

      return queueUpdate(item, limitedQuantity);
    },
    [optimisticState, queueUpdate]
  );

  /**
   * Increase item quantity by 1
   */
  const handleIncrease = useCallback(
    (item: any) => handleQuantityChange(item, 1),
    [handleQuantityChange]
  );

  /**
   * Decrease item quantity by 1
   */
  const handleDecrease = useCallback(
    (item: any) => handleQuantityChange(item, -1),
    [handleQuantityChange]
  );

  /**
   * Remove item entirely (set quantity to 0)
   */
  const handleRemove = useCallback(
    (item: any) => queueUpdate(item, 0),
    [queueUpdate]
  );

  /**
   * Direct method for setting quantity (for MiniCart compatibility)
   */
  const updateItemQuantity = useCallback(
    (newQuantity: number, item: any) => queueUpdate(item, newQuantity),
    [queueUpdate]
  );

  /**
   * Force processing of all pending updates immediately
   */
  const flushUpdates = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (Object.keys(pendingUpdatesRef.current).length > 0) {
      return processUpdates();
    }

    return Promise.resolve();
  }, [processUpdates]);

  /**
   * Get the optimistic quantity for an item
   */
  const getOptimisticQuantity = useCallback(
    (itemId: string, defaultQty: number) => {
      return optimisticState[itemId] !== undefined
        ? optimisticState[itemId]
        : defaultQty;
    },
    [optimisticState]
  );

  /**
   * Get the optimistic price for an item
   */
  const getOptimisticPrice = useCallback(
    (itemId: string, defaultPrice: number = 0) => {
      return optimisticPrices[itemId]?.basePrice !== undefined
        ? optimisticPrices[itemId].basePrice
        : defaultPrice;
    },
    [optimisticPrices]
  );

  /**
   * Get optimistic total price for an item
   */
  const getOptimisticTotalPrice = useCallback(
    (itemId: string, defaultPrice: number = 0, quantity: number = 1) => {
      if (optimisticPrices[itemId]?.totalPrice !== undefined) {
        const optimisticQty =
          optimisticState[itemId] !== undefined
            ? optimisticState[itemId]
            : quantity;
        return optimisticPrices[itemId].totalPrice * optimisticQty;
      }
      return defaultPrice * quantity;
    },
    [optimisticPrices, optimisticState]
  );

  /**
   * Check if there are any pending updates
   */
  const hasPendingUpdates = useCallback(() => {
    return Object.keys(pendingUpdatesRef.current).length > 0;
  }, []);

  // Return the appropriate API based on whether this is Cart or MiniCart
  return {
    // Common methods
    getOptimisticQuantity,
    hasPendingUpdates,
    flushUpdates,
    optimisticState,

    // Cart-specific methods (traditional API)
    handleIncrease,
    handleDecrease,
    handleRemove,
    handleQuantityChange,

    // MiniCart-specific methods (alternate API)
    updateItemQuantity,
    getOptimisticPrice,
    getOptimisticTotalPrice,
    optimisticPrices,
  };
}

export default useDebounceCartUpdates;
